import { Decimal } from 'decimal.js';
import { TOKEN_DECIMALS, FIAT_DECIMALS, UI_DISPLAY_DECIMALS } from '../constants/precision.js';

/**
 * Decimal helpers.
 *
 * This module wraps decimal.js with UO-specific rounding policies. Every
 * arithmetic operation involving money in this codebase MUST go through
 * `toDecimal()` first — never `Number(x)` or `parseFloat(x)`.
 *
 * Rounding policy: ROUND_DOWN (truncate) is chosen for all ledger operations
 * to match the legacy PHP system's `bcmath` behaviour and avoid accidentally
 * creating tokens out of thin air during division.
 */

Decimal.set({ precision: 50, rounding: Decimal.ROUND_DOWN, toExpPos: 40, toExpNeg: -40 });

export { Decimal };

/** Coerce a string or number to a Decimal. Throws on garbage input. */
export function toDecimal(value: string | number | Decimal): Decimal {
  return new Decimal(value);
}

/** Token-precision add: `a + b`, rounded to 18 fractional digits. */
export function addToken(a: string | Decimal, b: string | Decimal): string {
  return toDecimal(a).plus(toDecimal(b)).toFixed(TOKEN_DECIMALS, Decimal.ROUND_DOWN);
}

/** Token-precision subtract: `a - b`, rounded to 18 fractional digits. */
export function subToken(a: string | Decimal, b: string | Decimal): string {
  return toDecimal(a).minus(toDecimal(b)).toFixed(TOKEN_DECIMALS, Decimal.ROUND_DOWN);
}

/** Token-precision multiply: `a * b`. */
export function mulToken(a: string | Decimal, b: string | Decimal): string {
  return toDecimal(a).mul(toDecimal(b)).toFixed(TOKEN_DECIMALS, Decimal.ROUND_DOWN);
}

/** Token-precision divide: `a / b`. Throws on division by zero. */
export function divToken(a: string | Decimal, b: string | Decimal): string {
  const divisor = toDecimal(b);
  if (divisor.isZero()) throw new Error('division by zero');
  return toDecimal(a).div(divisor).toFixed(TOKEN_DECIMALS, Decimal.ROUND_DOWN);
}

/** `a < b` */
export function lt(a: string | Decimal, b: string | Decimal): boolean {
  return toDecimal(a).lt(toDecimal(b));
}
/** `a > b` */
export function gt(a: string | Decimal, b: string | Decimal): boolean {
  return toDecimal(a).gt(toDecimal(b));
}
/** `a == b` */
export function eq(a: string | Decimal, b: string | Decimal): boolean {
  return toDecimal(a).eq(toDecimal(b));
}

/** `a >= 0` */
export function isNonNegative(a: string | Decimal): boolean {
  return toDecimal(a).gte(0);
}

/** Fiat-precision (`DECIMAL(18,6)`) multiply — for EP total_fiat calc. */
export function mulFiat(a: string | Decimal, b: string | Decimal): string {
  return toDecimal(a).mul(toDecimal(b)).toFixed(FIAT_DECIMALS, Decimal.ROUND_DOWN);
}

/**
 * UI-display formatter. Truncates to `UI_DISPLAY_DECIMALS` fractional digits
 * and strips trailing zeros. Use this for table cells, card numbers, etc.
 *
 * Note: this MUST only be used for presentation. Never round-trip a UI string
 * back into the ledger.
 */
export function formatTokenForUI(value: string | Decimal): string {
  const d = toDecimal(value).toFixed(UI_DISPLAY_DECIMALS, Decimal.ROUND_DOWN);
  // strip trailing zeros: "12.3400" -> "12.34"
  return d.replace(/\.?0+$/, '');
}
